import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Typography, Paper, Box, CircularProgress, Alert, List, ListItem, 
  ListItemText, Divider, Button, TextField, FormControl, InputLabel, 
  Select, MenuItem, Grid 
} from '@mui/material';
import { getSolicitudById, addComment, getGestores, updateSolicitud } from '../services/api';

function SolicitudDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Estados de datos
  const [solicitud, setSolicitud] = useState(null);
  const [gestores, setGestores] = useState([]);
  
  // Estados de carga y error
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Estados de formulario
  const [nuevoComentario, setNuevoComentario] = useState('');
  const [selectedGestor, setSelectedGestor] = useState('');
  const [selectedEstatus, setSelectedEstatus] = useState(''); // <--- NUEVO ESTADO

  // Lista de estatus disponibles (Deben coincidir con Django)
  const estatusOptions = [
    { value: 'RECIBIDO', label: 'Recibido' },
    { value: 'EN_PROCESO', label: 'En Proceso' },
    { value: 'RESUELTO', label: 'Resuelto' },
    { value: 'CERRADO', label: 'Cerrado' },
  ];

  const fetchData = async () => {
    try {
      setLoading(true);
      const [solicitudRes, gestoresRes] = await Promise.all([
        getSolicitudById(id),
        getGestores()
      ]);
      setSolicitud(solicitudRes.data);
      setGestores(gestoresRes.data);
      
      // Pre-cargar los valores actuales
      setSelectedGestor(solicitudRes.data.asignado_a || '');
      setSelectedEstatus(solicitudRes.data.estatus || 'RECIBIDO'); // <--- CARGAR ESTATUS ACTUAL

    } catch (err) {
      setError('Error al cargar los datos.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [id]);

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!nuevoComentario.trim()) return;
    try {
      await addComment(id, nuevoComentario);
      setNuevoComentario('');
      fetchData(); // Recargar para ver el nuevo comentario
    } catch (err) {
      alert('Error al añadir el comentario.');
    }
  };

  const handleUpdateGeneral = async () => {
    try {
      // Enviamos tanto el gestor como el estatus
      const payload = {
        asignado_a: selectedGestor || null,
        estatus: selectedEstatus
      };
      
      await updateSolicitud(id, payload);
      alert('Cambios guardados correctamente.');
      fetchData();
    } catch (err) {
      alert('Error al actualizar la solicitud.');
      console.error(err);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!solicitud) return <Typography>No se encontró la solicitud.</Typography>;

  return (
    <Paper elevation={3} sx={{ p: 3, maxWidth: 900, mx: 'auto', mt: 3 }}>
      <Button onClick={() => navigate('/dashboard')} sx={{ mb: 2 }} variant="outlined">
        &larr; Volver al Dashboard
      </Button>

      <Typography variant="h4" gutterBottom color="primary">
        {solicitud.codigo_seguimiento}
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1"><strong>Tipo:</strong> {solicitud.tipo_solicitud}</Typography>
        <Typography variant="subtitle1"><strong>Ciudadano:</strong> {solicitud.nombre_ciudadano || 'Anónimo'}</Typography>
        <Typography variant="body1" sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          {solicitud.descripcion}
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* --- SECCIÓN DE GESTIÓN (MODIFICADA) --- */}
      <Box sx={{ mb: 4, p: 3, border: '1px solid #1976d2', borderRadius: 2, bgcolor: '#f0f7ff' }}>
        <Typography variant="h6" gutterBottom color="primary">Gestionar Caso</Typography>
        
        <Grid container spacing={2} alignItems="center">
          {/* Selector de Gestor */}
          <Grid item xs={12} md={5}>
            <FormControl fullWidth size="small">
              <InputLabel id="gestor-select-label">Asignar Responsable</InputLabel>
              <Select
                labelId="gestor-select-label"
                value={selectedGestor}
                label="Asignar Responsable"
                onChange={(e) => setSelectedGestor(e.target.value)}
              >
                <MenuItem value=""><em>-- Sin asignar --</em></MenuItem>
                {gestores.map((gestor) => (
                  <MenuItem key={gestor.id} value={gestor.id}>{gestor.username}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Selector de Estatus (NUEVO) */}
          <Grid item xs={12} md={5}>
            <FormControl fullWidth size="small">
              <InputLabel id="estatus-select-label">Cambiar Estatus</InputLabel>
              <Select
                labelId="estatus-select-label"
                value={selectedEstatus}
                label="Cambiar Estatus"
                onChange={(e) => setSelectedEstatus(e.target.value)}
              >
                {estatusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Botón de Guardar */}
          <Grid item xs={12} md={2}>
            <Button 
              onClick={handleUpdateGeneral} 
              variant="contained" 
              color="primary" 
              fullWidth
              sx={{ height: '40px' }}
            >
              Guardar
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Typography variant="h5" gutterBottom>Comentarios Internos</Typography>
      <List sx={{ bgcolor: 'background.paper', mb: 2 }}>
        {(solicitud.comentarios || []).map((comentario, index) => (
          <React.Fragment key={comentario.id}>
            <ListItem alignItems="flex-start">
              <ListItemText
                primary={comentario.texto}
                secondary={
                  <Typography component="span" variant="body2" color="text.secondary">
                    — {comentario.autor} • {new Date(comentario.fecha_creacion).toLocaleString()}
                  </Typography>
                }
              />
            </ListItem>
            {index < solicitud.comentarios.length - 1 && <Divider component="li" />}
          </React.Fragment>
        ))}
        {(!solicitud.comentarios || solicitud.comentarios.length === 0) && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>No hay comentarios aún.</Typography>
        )}
      </List>

      {/* Formulario de Comentarios */}
      <Box component="form" onSubmit={handleAddComment} sx={{ mt: 2, mb: 4 }}>
        <TextField
          fullWidth
          multiline
          rows={3}
          variant="outlined"
          placeholder="Escribir nota interna o resolución..."
          value={nuevoComentario}
          onChange={(e) => setNuevoComentario(e.target.value)}
        />
        <Button type="submit" variant="contained" sx={{ mt: 1 }}>
          Añadir Comentario
        </Button>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" gutterBottom color="text.secondary">Auditoría del Caso</Typography>
      <List dense>
        {(solicitud.historial || []).map((cambio, index) => (
          <ListItem key={index}>
            <ListItemText
              primary={`Campo '${cambio.campo}' cambió de '${cambio.valor_anterior || 'Vacío'}' a '${cambio.valor_nuevo}'`}
              secondary={`${new Date(cambio.fecha_modificacion).toLocaleString()} — Por: ${cambio.usuario || 'Sistema'}`}
            />
          </ListItem>
        ))}
      </List>
    </Paper>
  );
}

export default SolicitudDetailPage;
